import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { History, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtTs = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch {
    return iso;
  }
};

export const ReportDownloadLog = ({ reportKey, refreshToken = 0 }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token');
    setLoading(true);
    axios
      .get(`${API}/reports/download-logs`, {
        params: { report_key: reportKey, limit: 30 },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => { if (!cancelled) setLogs(res.data || []); })
      .catch(() => { if (!cancelled) setLogs([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [reportKey, refreshToken]);

  return (
    <Card className="border-slate-200" data-testid="report-download-log">
      <CardContent className="p-5">
        <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-3">
          <History size={16} className="text-slate-500" />
          Download Log
          <span className="text-xs font-normal text-slate-500">— who exported this report and when</span>
        </h2>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-3">
            <Loader2 size={15} className="animate-spin" /> Loading log…
          </div>
        ) : logs.length === 0 ? (
          <p className="text-sm text-slate-400 italic py-2" data-testid="download-log-empty">
            No downloads recorded yet.
          </p>
        ) : (
          <div className="divide-y divide-slate-100">
            {logs.map((l) => (
              <div key={l.id} className="flex items-center justify-between py-2 gap-3" data-testid="download-log-row">
                <div className="min-w-0">
                  <span className="text-sm font-medium text-slate-800">{l.user_name || 'Unknown user'}</span>
                  <span className="text-xs text-slate-500 ml-2 truncate">{l.user_email}</span>
                  {l.user_role && <span className="text-[10px] uppercase text-slate-400 ml-2">{l.user_role}</span>}
                  {l.filters && (
                    <div className="text-[11px] text-slate-500 mt-0.5 truncate" data-testid="download-log-filters"
                      title={Object.entries(l.filters).map(([k, v]) => `${k}: ${v}`).join(' · ')}>
                      {Object.keys(l.filters).length
                        ? Object.entries(l.filters).map(([k, v]) => `${k.replace(/_/g, ' ')}: ${String(v).replace(/\|/g, ', ')}`).join(' · ')
                        : 'No filters (all records)'}
                      {l.row_count !== undefined && ` — ${l.row_count} rows`}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <Badge variant="outline" className="uppercase text-[10px] bg-slate-50">{l.format}</Badge>
                  <span className="text-xs text-slate-600 tabular-nums">{fmtTs(l.downloaded_at)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ReportDownloadLog;
